import * as React from "react";
import { Slot } from "radix-ui";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { twMergeConfig } from "@repo/utils/twMergeConfig";

export const title = tv(
  {
    base: "ui:font-display ui:font-bold ui:transition-colors",
    variants: {
      variant: {
        microheading: "ui:text-microheading",
        subheading: "ui:text-subheading",
        heading: "ui:text-heading",
        subtitle: "ui:text-subtitle",
        titlePage: "ui:text-titlePage",
        titleHero: "ui:text-titleHero",
        number: "ui:text-number",
      },
      mdVariant: {
        microheading: "ui:md:text-microheading",
        subheading: "ui:md:text-subheading",
        heading: "ui:md:text-heading",
        subtitle: "ui:md:text-subtitle",
        titlePage: "ui:md:text-titlePage",
        titleHero: "ui:md:text-titleHero",
        number: "ui:md:text-number",
      },
      xlVariant: {
        microheading: "ui:xl:text-microheading",
        subheading: "ui:xl:text-subheading",
        heading: "ui:xl:text-heading",
        subtitle: "ui:xl:text-subtitle",
        titlePage: "ui:xl:text-titlePage",
        titleHero: "ui:xl:text-titleHero",
        number: "ui:xl:text-number",
      },
      color: {
        primary: "ui:text-textPrimary",
        secondary: "ui:text-textSecondary",
        light: "ui:text-textLight",
        accent: "ui:text-textAmplified",
        inverted: "ui:text-textInverted",
      },
      mdColor: {
        primary: "ui:md:text-textPrimary",
        secondary: "ui:md:text-textSecondary",
        light: "ui:md:text-textLight",
        accent: "ui:md:text-textAmplified",
        inverted: "ui:md:text-textInverted",
      },
      xlColor: {
        primary: "ui:xl:text-textPrimary",
        secondary: "ui:xl:text-textSecondary",
        light: "ui:xl:text-textLight",
        accent: "ui:xl:text-textAmplified",
        inverted: "ui:xl:text-textInverted",
      },
      textWrap: {
        balance: "ui:text-balance",
        pretty: "ui:text-pretty",
        normal: "",
      },
    },
    defaultVariants: {
      variant: "heading",
      color: "primary",
      textWrap: "normal",
    },
  },
  {
    twMergeConfig,
  },
);

export type TitleProps = VariantProps<typeof title> & {
  ref?: React.ForwardedRef<HTMLHeadingElement>;
  as?: "div" | "p" | "h1" | "h2" | "h3";
  asChild?: boolean;
  className?: string;
  fontFeatures?: string[];
  children?: React.ReactNode;
};

export function Title({
  ref,
  as,
  asChild,
  className,
  variant,
  mdVariant,
  xlVariant,
  color,
  mdColor,
  xlColor,
  textWrap,
  fontFeatures,
  ...props
}: TitleProps) {
  const Component = asChild ? Slot.Root : as || "p";
  return (
    <Component
      {...props}
      ref={ref}
      className={cn(
        title({
          variant,
          mdVariant,
          xlVariant,
          color,
          mdColor: mdColor || color,
          xlColor: xlColor || mdColor || color,
          textWrap,
        }),
        className,
      )}
      style={
        fontFeatures
          ? ({
              "--ui-font-display--font-feature-settings": fontFeatures
                .map((f) => `"${f}" on`)
                .join(","),
              ...("style" in props ? (props as { style?: React.CSSProperties }).style : {}),
            } as React.CSSProperties)
          : undefined
      }
    />
  );
}
